import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import './custom-quill.css';
import 'quill/dist/quill.snow.css';
import Quill, { QuillOptions, Delta, EmitterSource } from 'quill';

export type EditorContent = {
    html: string;
    text: string;
};

type QuillEditorHandle = {
    getEditor: () => Quill | null;
    getContent: () => EditorContent | null;
    setContent: (content: string) => void;
    clearContent: () => void;
    focus: () => void;
    blur: () => void;
};

type QuillEditorProps = {
    value: string;
    onTextChange: (content: EditorContent) => void;
    placeholder?: string;
    readOnly?: boolean;
    onSelectionChange?: (
        range: Range | null,
        oldRange: Range | null,
        source: EmitterSource,
    ) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    onHeightChange?: () => void;
    className?: string;
    style?: React.CSSProperties;
    showToolbar?: boolean;
    autoFocus?: boolean;
};

export const QuillEditor = forwardRef<QuillEditorHandle, QuillEditorProps>(
    (
        {
            value,
            placeholder = '',
            readOnly = false,
            onTextChange,
            onSelectionChange,
            onFocus,
            onBlur,
            onHeightChange,
            className = '',
            style,
            showToolbar = true,
            autoFocus = false,
        },
        ref,
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const editorRef = useRef<HTMLDivElement>(null);
        const quillRef = useRef<Quill | null>(null);
        const onTextChangeRef = useRef(onTextChange);
        const [isReady, setIsReady] = useState(false);

        // Default modules configuration
        const modules: QuillOptions['modules'] = {
            toolbar: {
                container: '#editor-toolbar',
            },
            clipboard: {
                matchVisual: false,
            },
            history: {
                delay: 1000,
                maxStack: 50,
                userOnly: true,
            },
        };

        const formats = ['bold', 'italic', 'underline', 'strike'];

        const addAccessibilityAttributes = (quillInstance: Quill) => {
            const toolbar = quillInstance.getModule('toolbar') as { container: Element };
            if (!toolbar) return;

            // Add role and aria-label to toolbar container
            const toolbarContainer: Element = toolbar.container;
            if (toolbarContainer && !toolbarContainer.getAttribute('role')) {
                toolbarContainer.setAttribute('role', 'toolbar');
                toolbarContainer.setAttribute('aria-label', 'Text formatting options');
            }

            // Add ARIA labels to toolbar buttons
            const buttonMappings = {
                '.ql-bold': 'Bold',
                '.ql-italic': 'Italic',
                '.ql-underline': 'Underline',
                '.ql-strike': 'Strikethrough',
            };

            Object.entries(buttonMappings).forEach(([selector, label]) => {
                const button = toolbarContainer.querySelector(selector);
                if (button) {
                    button.setAttribute('aria-label', label);
                    button.setAttribute('title', label);
                    // Add tabindex for keyboard navigation
                    if (!button.getAttribute('tabindex')) {
                        button.setAttribute('tabindex', '0');
                    }
                }
            });
        };

        // Quill init
        useEffect(() => {
            if (!containerRef.current || !editorRef.current) return;

            const options: QuillOptions = {
                modules,
                formats,
                placeholder,
                readOnly,
                theme: 'snow',
            };

            const quill = new Quill(editorRef.current, options);
            quillRef.current = quill;

            if (quill) {
                addAccessibilityAttributes(quillRef.current);
            }

            const handleTextChange = (delta: Delta, oldDelta: Delta, source: EmitterSource) => {
                if (delta !== oldDelta && source !== 'silent') {
                    const content: EditorContent = {
                        html: quill.root.innerHTML,
                        text: quill.getText(),
                    };
                    onTextChangeRef.current?.(content);
                }
            };

            const handleSelectionChange = (
                range: Range | null,
                oldRange: Range | null,
                source: EmitterSource,
            ) => {
                if (onSelectionChange) {
                    onSelectionChange(range, oldRange, source);
                }

                if (range && !oldRange) {
                    onFocus?.();
                } else if (!range && oldRange) {
                    onBlur?.();
                }
            };

            quill.on(Quill.events.TEXT_CHANGE, handleTextChange);
            quill.on(Quill.events.SELECTION_CHANGE, handleSelectionChange);

            setIsReady(true);

            // Cleanup
            return () => {
                quill.off(Quill.events.TEXT_CHANGE, handleTextChange);
                quill.off(Quill.events.SELECTION_CHANGE, handleSelectionChange);
                if (containerRef.current) {
                    // eslint-disable-next-line react-hooks/exhaustive-deps
                    containerRef.current.innerHTML = '';
                }
                quillRef.current = null;
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        useEffect(() => {
            if (!quillRef.current || !isReady || value === undefined) return;

            const quill = quillRef.current;

            const currentHTML = quill.root.innerHTML;
            if (currentHTML !== value) {
                const selection = quill.getSelection();
                quill.clipboard.dangerouslyPasteHTML(value);
                if (selection) {
                    quill.setSelection(selection);
                }
            }
        }, [value, isReady]);

        // Auto-focus and move caret to end
        useEffect(() => {
            if (autoFocus && quillRef.current && isReady) {
                const quill = quillRef.current;
                quill.focus();

                const length = quill.getLength();
                quill.setSelection(length - 1, 0);
            }
        }, [autoFocus, isReady]);

        // Observer pour détecter les changements de hauteur
        useEffect(() => {
            if (!editorRef.current || !onHeightChange) return;

            const resizeObserver = new ResizeObserver(() => {
                onHeightChange();
            });

            resizeObserver.observe(editorRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        }, [onHeightChange]);

        useEffect(() => {
            if (!quillRef.current || !isReady) return;
            quillRef.current.enable(!readOnly);
        }, [readOnly, isReady]);

        useImperativeHandle(
            ref,
            () => ({
                getEditor: () => quillRef.current,

                getContent: () => {
                    if (!quillRef.current) return null;
                    return {
                        html: quillRef.current.root.innerHTML,
                        text: quillRef.current.getText(),
                        delta: quillRef.current.getContents(),
                        length: quillRef.current.getLength(),
                    };
                },

                setContent: (content: string) => {
                    if (!quillRef.current) return;
                    quillRef.current.clipboard.dangerouslyPasteHTML(content);
                },

                getText: () => {
                    return quillRef.current?.getText() || '';
                },

                clearContent: () => {
                    quillRef.current?.setText('');
                },

                focus: () => {
                    quillRef.current?.focus();
                },

                blur: () => {
                    quillRef.current?.blur();
                },
            }),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [isReady],
        );

        return (
            <div ref={containerRef} className={`quill-container ${className}`} style={style}>
                <div
                    id="editor-toolbar"
                    className="ql-toolbar"
                    style={{ display: showToolbar ? 'flex' : 'none' }}
                >
                    <span className="ql-formats">
                        <button className="ql-bold" />
                        <button className="ql-italic" />
                        <button className="ql-underline" />
                        <button className="ql-strike" />
                    </span>
                </div>
                <div ref={editorRef} />
            </div>
        );
    },
);
